import {Table, TableProps} from "@/components/antd/table/Table";
import {ACTION_METHOD_OBJ} from "@/constants";
import {SESSION_IN_DAY} from "@/modules/edu/pages/operation/work-time/constants";
import {API} from "@/network/http";
import {CRUD_ACTION} from "@/types";
import {getHourNumberByString} from "@/utils";
import {CreateSchoolHourDto, SchoolHourDto} from "@/__generated__/api-v1";
import {DeleteTwoTone, EditTwoTone} from "@ant-design/icons";
import type {ActionType, ProColumns} from "@ant-design/pro-table";
import {Badge, notification, Popconfirm, Space, Tooltip} from "antd";
import {FC, useRef, useState} from "react";
import {DEFINE_MODULE} from "../constants";
import UpsertForm from "./UpsertForm";

const List: FC = () => {
  const [loadingBtn, setLoadingBtn] = useState<boolean>(false);
  const [isCreate, setCreate] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [values, setValues] = useState<any>();
  const actionRef = useRef<ActionType>();

  const [notifyApi, contextHolder] = notification.useNotification();

  const onClickEditButton = async (id: string) => {
    const detailData = await API.schoolHours.schoolHourControllerGetOne(id);
    setValues(detailData.data.data);
    setUpdate(true);
  };

  const onClickDeleteButton = async (id: string) => {
    try {
      await API.schoolHours.schoolHourControllerDeleteOne(id);
      notifyApi.success({message: `${ACTION_METHOD_OBJ['DELETE']} ${DEFINE_MODULE.name} thành công`});
      actionRef.current?.reload();
    }
    catch (err: any) {
      notifyApi?.error({
        message: err.error?.message || err.message || err,
      });
    }
  };

  const onSubmit = async (val: any, type: CRUD_ACTION, _id?: string) => {
    try {
      const payload: CreateSchoolHourDto = {
        ...val,
        startHour: getHourNumberByString(val.startHourName),
        endHour: getHourNumberByString(val.endHourName),
        timeFrame: `${val.startHourName.hours()}:${val.startHourName.minutes()} - ${val.endHourName.hours()}:${val.endHourName.minutes()}`,
      };
      setLoadingBtn(true);
      const reqData =
        type === "CREATE"
          ? await API.schoolHours.schoolHourControllerCreateOne(payload)
          : await API.schoolHours.schoolHourControllerUpdateOne(_id!, payload);
      if (reqData && reqData.data) {
        if (type === "CREATE") setCreate(false);
        if (type === "UPDATE") setUpdate(false);
        notifyApi.success({
          message: "Thông báo",
          description: `${ACTION_METHOD_OBJ[type]} thành công`,
          placement: "topRight",
        });
        actionRef.current?.reload();
      }
    }
    catch (err: any) {
      notifyApi?.error({
        message: err.error?.message || err.message || err,
      });
    }
    finally {
      setLoadingBtn(false);
    }
  };

  const fetchData = async (queries: Parameters<typeof API.schoolHours.schoolHourControllerGetMany>["0"]) => {
    return (await API.schoolHours.schoolHourControllerGetMany({...queries})).data;
  };

  const onDeleteMany = async (ids: string[]) => {
    try {
      await API.schoolHours.schoolHourControllerDeleteMany({ids});
      notifyApi.success({message: `${ACTION_METHOD_OBJ['DELETE']} ${DEFINE_MODULE.name} thành công`});
      actionRef.current?.reload();
    }
    catch (err: any) {
      notifyApi?.error({
        message: err.error?.message || err.message || err,
      });
    }
  };

  const onArchiveMany = async (ids: string[]) => {
    try {
      await API.schoolHours.schoolHourControllerUpdateMany({ids, isArchived: true});
      notifyApi.success({message: `${ACTION_METHOD_OBJ['ARCHIVE']} ${ids.length} ${DEFINE_MODULE.name} thành công`});
      actionRef.current?.reload();
    }
    catch (err: any) {
      notifyApi?.error({
        message: err.error?.message || err.message || err,
      });
    }
  };

  const onUnarchiveMany = async (ids: string[]) => {
    try {
      await API.schoolHours.schoolHourControllerUpdateMany({ids, isArchived: false});
      notifyApi.success({message: `${ACTION_METHOD_OBJ['UNARCHIVE']} ${ids.length} ${DEFINE_MODULE.name} thành công`});
      actionRef.current?.reload();
    }
    catch (err: any) {
      notifyApi?.error({
        message: err.error?.message || err.message || err,
      });
    }
  };

  const columns: ProColumns<SchoolHourDto>[] = [
    {
      dataIndex: "timeFrame",
      title: "Khung giờ",
      search: false,
    },
    {
      dataIndex: "startHourName",
      title: "Giờ bắt đầu",
      search: false,
    },
    {
      dataIndex: "endHourName",
      title: "Giờ kết thúc",
      search: false,
    },
    {
      dataIndex: "studySchedule",
      title: "Buổi",
      renderText: (_, entity) => {
        let name = "";
        switch (entity.studySchedule) {
          case "MORNING": {
            name = "Sáng";
            break;
          }
          case "AFTERNOON": {
            name = "Chiều";
            break;
          }
          case "EVENING": {
            name = "Tối";
            break;
          }
        }
        return <Badge>{name}</Badge>;
      },
      search: false
    },
    {
      hideInTable: true,
      dataIndex: "studySchedule",
      title: "Buổi",
      valueType: "select",
      valueEnum: SESSION_IN_DAY,
    },
    {
      title: "Thao tác",
      align: "center",
      search: false,
      fixed: "right",
      width: 80,
      render: (_, entity) => {
        return (
          <Space size={10}>
            <Tooltip title={`Cập nhật ${DEFINE_MODULE.name}`}>
              <EditTwoTone onClick={() => onClickEditButton(entity._id)} />
            </Tooltip>
            <Tooltip title={`Xoá ${DEFINE_MODULE.name}`}>
              <Popconfirm
                title={`Bạn có chắc rằng muốn xoá ${DEFINE_MODULE.name} này không?`}
                onConfirm={() => onClickDeleteButton(entity._id)}
                okText="Có"
                cancelText="Không"
              >
                <DeleteTwoTone twoToneColor={"red"} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const protableProps: TableProps = {
    actionRef,
    DEFINE_MODULE,
    contextHolder,
    fetchData,
    columns,
    deleteMany: onDeleteMany,
    archiveMany: onArchiveMany,
    unArchiveMany: onUnarchiveMany,
    createForm: {
      isCreate,
      setCreate,
      component: (
        <UpsertForm
          notify={notifyApi}
          key="CREATE"
          onCancel={() => setCreate(false)}
          modalVisible={isCreate}
          onSubmit={onSubmit}
          actionType="CREATE"
          loadingBtn={loadingBtn}
        />
      ),
    },
    updateForm: {
      isUpdate,
      component: (
        <UpsertForm
          notify={notifyApi}
          key="UPDATE"
          onCancel={() => setUpdate(false)}
          modalVisible={isUpdate}
          onSubmit={onSubmit}
          actionType="UPDATE"
          loadingBtn={loadingBtn}
          values={values}
        />
      ),
    },
  };

  return <Table {...protableProps} />;
};

export default List;
